function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 0) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.bindPulseMenuExtras();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;

    /* Measure the visible header bar (the <header.header> element
       inside .header-wrapper) and write its height to `--pulse-menu-pad-top`.
       The panel uses this as its top padding so the logo + cart icons
       (which sit on top via z-index) align with the panel's bg behind
       them. Falls back to 80px in CSS if this property isn't set. */
    const visibleHeader =
      document.querySelector('.header-wrapper > header.header') ||
      document.querySelector('.section-header header.header') ||
      this.header;
    const headerHeight = parseInt(visibleHeader.getBoundingClientRect().height);
    if (headerHeight > 0) {
      document.documentElement.style.setProperty('--pulse-menu-pad-top', `${headerHeight}px`);
    }
    /* Legacy CSS variable kept for back-compat with other paths. */
    const headerBottom = parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset);
    document.documentElement.style.setProperty('--header-bottom-position', `${headerBottom}px`);
    this.header.classList.add('menu-open');
    document.body.classList.add('menu-open');
    document.documentElement.classList.add('pulse-menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
      /* `body.pulse-menu-open` triggers the panel's visible state.
         Keying off the body class (not the relocated panel's parent
         `<details>`, which it no longer descends from after
         relocation) is what makes the CSS slide animation fire. */
      document.body.classList.add('pulse-menu-open');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    document.body.classList.remove('menu-open');
    document.body.classList.remove('pulse-menu-open');
    document.documentElement.classList.remove('pulse-menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };

  /* ── Pulse mobile-menu extras ──
     Wire up: tap-outside-to-close and one-accordion-at-a-time, both
     driven by the new header-extension drop-down design. These are
     additive — they don't replace the Dawn open/close path; they just
     hook into the same `<details id="Details-menu-drawer-container">`
     and its nested `<details data-pulse-menu-accordion>` children. */
  bindPulseMenuExtras() {
    const root = this.querySelector('#Details-menu-drawer-container');
    if (!root) return;

    /* Tap-outside-to-close.
       Capture-phase pointerdown so we fire BEFORE any inner element's
       click (and BEFORE a same-tap link navigation). If the tap lands
       inside the panel, the summary, or any open bottom-sheet (the
       locale picker overlays render outside the menu DOM), bail. */
    /* Tap-outside-to-close fires on `click` (bubble phase), NOT
       `pointerdown`. Why this matters:
         If pointerdown closes the menu, DOM mutations from the close
         (body class removals, layout reflows) happen DURING the tap
         sequence. iOS Safari cancels the subsequent click if the
         tap target's computed state changed mid-flight — so tapping
         the search icon (or cart, or any header button) while the
         menu is open would close the menu but the icon's OWN click
         handler never fired. Result: user had to tap twice — once
         to "close" the menu, again to open search.
         Moving to `click` (bubble) means: the tap's target gets its
         own click handler FIRST (search opens), then bubble reaches
         the document and our handler closes the menu. Both happen
         in one user tap. */
    document.addEventListener(
      'click',
      (e) => {
        if (!root.open) return;
        if (!e.target || !e.target.closest) return;
        if (e.target.closest('#menu-drawer')) return;
        if (e.target.closest('header-drawer summary')) return;
        if (e.target.closest('.pulse-sheet')) return;
        if (e.target.closest('.pulse-mobile-locale')) return;
        if (e.target.closest('.pulse-locale-popup')) return;
        const summary = root.querySelector(':scope > summary');
        this.closeMenuDrawer(e, summary);
      },
      false
    );

    /* One-accordion-at-a-time.
       When any `<details data-pulse-menu-accordion>` opens, close all
       its siblings at the same depth. Uses the `toggle` event (fires
       on every state change) and walks up to the nearest list to find
       siblings — works for nested sublists too if you ever add them. */
    root.addEventListener('toggle', (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement)) return;
      if (!d.hasAttribute('data-pulse-menu-accordion')) return;
      if (!d.open) return;
      const li = d.closest('li');
      if (!li) return;
      const parentList = li.parentElement;
      if (!parentList) return;
      parentList.querySelectorAll(':scope > li > details[data-pulse-menu-accordion]').forEach((other) => {
        if (other !== d && other.open) other.open = false;
      });
    }, true);
  }
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.scrollEle = this.querySelector(".slider__ele");
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.scrollEle.style.left = `${100 * this.currentPage / this.totalPages}%`;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }
  initSlider() {
    // Swiper init disabled entirely. The slider mode now uses native
    // CSS overflow-x scrolling (see section-related-products.css)
    // — same approach the featured-collection section uses, which
    // resolves three issues at once:
    //   1. No flicker: native scroll doesn't fire hover/mouseover
    //      events during a swipe, so the + → panel state doesn't
    //      toggle as cards pass.
    //   2. Tap + opens panel: Swiper was intercepting taps and
    //      could end up suppressing the click before our delegated
    //      handler ran.
    //   3. Size-pill swipe works: with Swiper gone, the inner
    //      `.card-grid-options-size` scroller is no longer fighting
    //      Swiper for the horizontal gesture.
    return;

    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', () => this.initSlider(), { once: true });
      return;
    }

    const sliderEl = this.querySelector('.related__products__slider');
    if (!sliderEl) return;

    // Read the grid-spacing variables FROM THE WRAPPER, not from root.
    // The global `enable_card_padding` toggle overrides these vars to 0
    // on `.grid.product-grid` (which the wrapper has) when spacing is
    // off, but leaves root's value untouched. Reading from root would
    // always give the original spacing regardless of the toggle.
    const wrapperEl = sliderEl.querySelector('.swiper-wrapper');
    const readStyles = wrapperEl ? getComputedStyle(wrapperEl) : getComputedStyle(document.documentElement);
    const spaceMobile = parseFloat(readStyles.getPropertyValue('--grid-mobile-horizontal-spacing')) || 0;
    const spaceDesktop = parseFloat(readStyles.getPropertyValue('--grid-desktop-horizontal-spacing')) || 0;

    new Swiper(sliderEl, {
      loop: false,
      slidesPerView: 'auto',
      spaceBetween: spaceMobile,
      breakpoints: {
        750: { spaceBetween: spaceDesktop },
      },
    });
  }
  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      // Large rootMargin so the fetch fires well before the section scrolls
      // into view — the swap completes while the user is still looking elsewhere.
      { rootMargin: '0px 0px 1200px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');
        const hasNewContent = recommendations ? recommendations.innerHTML.trim().length > 0 : false;
        const hasGridItem = html.querySelector('.grid__item');

        const finish = () => {
          if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
          }
          if (hasGridItem) {
            this.classList.add('product-recommendations--loaded');
          }
          setTimeout(() => { this.initSlider(); }, 100);
        };

        if (!recommendations) { finish(); return; }

        if (hasNewContent) {
          // Element starts empty on page load, so just fade the new content in.
          // No fade-out needed — there's nothing to swap away from.
          this.style.opacity = '0';
          this.innerHTML = recommendations.innerHTML;
          void this.offsetHeight; // force reflow so transition fires
          this.style.transition = 'opacity 0.3s ease';
          this.style.opacity = '1';
          finish();
        } else {
          finish();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class ScrollBar extends HTMLElement{
  constructor(){
    super();
    this.scrollEle = this.querySelector(".scroll__ele_moveable");
    this.scrollContainer = document.querySelector("#product__media__popup");
  }
  connectedCallback(){
    this.moveEle();
  }
  moveEle() {
    const trackWidth = this.scrollEle.parentElement.clientWidth;
    const thumbWidth = this.scrollEle.clientWidth;

    this.scrollContainer.addEventListener("scroll", () => {
      const maxScroll = this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;
      const maxThumbMove = trackWidth - thumbWidth;

      const ratio = this.scrollContainer.scrollLeft / maxScroll;
      const left = ratio * maxThumbMove;
      this.scrollEle.style.left = left + "px";
    });
  }
  moveContainer(){
    this.scrollEle.addEventListener("mousedown",()=>{
      // const 
    })
  }
}
if (!customElements.get('scroll-bar')) {
  customElements.define('scroll-bar', ScrollBar);
}

if(!customElements.get("variant-image")){
  customElements.define("variant-image",class VariantImage extends HTMLElement{
    constructor(){
      super();
      this.productUrl = this.dataset.product;
      this.mediaHTML = this.dataset.images;
      this.variantsHTML = this.dataset.variants;
      this.mainParent = this.closest(".card--media");
      this.mainMedia = this.mainParent.querySelector(".media");
      this.siblings = this.mainParent.querySelectorAll("variant-image");
      this.badgeHTML = this.dataset.badge;
      this.mainBadge = this.mainParent.querySelector(".card__badge");
      this.anchor = this.mainParent.querySelectorAll("a");
      this.mainVariants = this.mainParent.querySelector(".card-options-wrapper");
    }
    connectedCallback(){
      // No hover preview on either mode. Hover-swap-without-commit
      // (image changes but nothing actually selects) is a half-state
      // that confused users in variant-color mode and added
      // inconsistency between the two modes. Click is the single
      // commit path in BOTH modes now: click to select, click to
      // see the swap. Bonus: mobile (where hover is synthetic /
      // unreliable) and desktop behave identically.

      // Click: same as hover — pure visual preview. Neither mode commits
      // anything here:
      //  • Metafield mode (`card_variant-image`): each swatch represents a
      //    DIFFERENT product. Click previews the image and updates where
      //    the title/image links point, but does NOT navigate or change
      //    the cart pipeline. + still adds the ORIGINAL card's product
      //    because renderUI no longer swaps variants_data (see below).
      //    Users who want the previewed product navigate via the image
      //    or title link, which now point to that product.
      //  • Variant-color mode (no `card_variant-image`): click previews
      //    the image; the SEPARATE QuickBuy.handleOptionClick listener
      //    (attached in connectedCallback) updates selectedOptions for
      //    the chosen color so size-pill / + commits resolve correctly.
      this.addEventListener("click", (e) => {
        // ── Single source of truth on color swatch tap ──
        // QuickBuy's connectedCallback iterates `this.options` (which is
        // `mainParent.querySelectorAll("[data-value]")`) and attaches its
        // OWN click → handleOptionClick listener to every option — INCLUDING
        // color-item swatches. So without this stop, two listeners fire on
        // the same tap: variant-image's (here) AND quick-buy's. They each
        // mutate `selectedOptions` and re-run `refreshAvailability`, in
        // listener-attachment order. The combined effect was non-deterministic
        // on mobile — the user reported "first tap doesn't update, second
        // tap does", which is exactly the pattern when two handlers race
        // and one leaves intermediate state. stopImmediatePropagation kills
        // QuickBuy's handler on the same element so variant-image's logic
        // below is the ONLY mutation path for color taps.
        e.stopImmediatePropagation();
        this.renderUI();
        this.handleState();

        if (this.classList.contains("card_variant-image")) return;

        // ── Variant-color mode: FULL QuickBuy state reset ──
        // The previous "patch" only updated selectedOptions[colorIdx] and
        // left selectedOptions[sizeIdx] = whatever size was prefilled for
        // the OLD color. If that size doesn't exist (or is sold out) for
        // the new color, refreshAvailability marks pills disabled against
        // a stale, incompatible selection — appearing on mobile as "sizes
        // don't update" because the user sees disabled pills where they
        // shouldn't be (or vice versa).
        //
        // Fix: reset selectedOptions to a CLEAN slate, then re-prefill
        // every non-color axis (sizes) from the first AVAILABLE variant
        // matching the newly-tapped color. Now refresh has consistent,
        // correct state.
        const group = this.closest && this.closest(".card-grid-options-size");
        const colorValue = this.getAttribute("data-value");
        if (!group || !colorValue) return;
        const colorIdx = +group.dataset.option;
        const qb = this.mainParent && this.mainParent.querySelector("quick-buy.card-options-wrapper");
        if (!Number.isFinite(colorIdx) || !qb) return;

        // Re-query options in case anything got swapped. With variant-color
        // mode quick-buy.innerHTML isn't swapped, but the re-query is cheap
        // insurance against future regressions.
        qb.options = qb.mainParent.querySelectorAll("[data-value]");

        const lcColor = colorValue.toLowerCase();
        const selection = {};
        selection[colorIdx] = lcColor;

        if (Array.isArray(qb.variants) && qb.variants.length) {
          // Find first AVAILABLE variant whose colorIdx option matches the
          // newly-tapped color. Fall back to the first variant of that
          // color if every variant for it is sold out (still gives a
          // sensible size prefill — refresh will then disable everything).
          const matchColor = function (v) {
            return v && Array.isArray(v.options) &&
              (v.options[colorIdx] || "").toLowerCase() === lcColor;
          };
          const seed = qb.variants.find(function (v) { return matchColor(v) && v.available; })
                     || qb.variants.find(matchColor);
          if (seed && Array.isArray(seed.options)) {
            seed.options.forEach(function (opt, i) {
              if (i === colorIdx) return;
              if (typeof opt === "string") selection[i] = opt.toLowerCase();
            });
          }
        }
        qb.selectedOptions = selection;

        // Clear is-active on SIZE pills (color-item is-active is managed
        // by handleState's current-active-color class). Without this the
        // previously-selected size still looks selected, which contradicts
        // the new selectedOptions[sizeIdx] we just prefilled.
        qb.querySelectorAll(".card-grid-options-size").forEach(function (g) {
          if (+g.dataset.option === colorIdx) return;
          g.querySelectorAll("[data-value]").forEach(function (p) {
            p.classList.remove("is-active");
          });
        });

        if (typeof qb.refreshAvailability === "function") qb.refreshAvailability();
      });
    }

    renderUI(){
      // Re-read attributes FRESH each render — iOS Safari has known
      // staleness issues with custom-element `dataset` after innerHTML
      // mutations. Using getAttribute guarantees we get the live value.
      const mediaHTML = this.getAttribute("data-images") || "";
      const badgeHTML = this.getAttribute("data-badge") || "";
      const productUrl = this.getAttribute("data-product") || "";
      const variantsHTML = this.getAttribute("data-variants") || "";
      const cardAvail = this.getAttribute("data-card-available");

      // Re-query DOM references LIVE each render. The constructor-time
      // refs (this.mainMedia/mainBadge/anchor) can become detached if
      // anything else mutates the card subtree between renders — e.g.,
      // a section:load reload, a swipe-gallery rebuild, or an earlier
      // variant-image's swap. A detached ref still accepts innerHTML
      // writes but nothing renders, which produced the intermittent
      // "first tap doesn't update image, second tap does" bug. Re-query
      // is cheap and always points at the live element.
      const liveParent = this.closest(".card--media") || this.mainParent;
      const liveMedia = liveParent ? liveParent.querySelector(".media") : null;
      const liveBadge = liveParent ? liveParent.querySelector(".card__badge") : null;
      const liveAnchors = liveParent ? liveParent.querySelectorAll("a") : null;
      if (liveParent) this.mainParent = liveParent;

      // Visual swap on every mode.
      if (liveMedia) liveMedia.innerHTML = mediaHTML;
      if (liveBadge) liveBadge.innerHTML = badgeHTML;
      if (liveAnchors) liveAnchors.forEach(a => a.href = productUrl);

      // Retarget the card's image-click navigation to the previewed
      // product (read by the doc-level click handler in global.js).
      const cardMedia = this.mainParent && this.mainParent.querySelector(".card__media[data-product-url]");
      if (cardMedia && productUrl) {
        cardMedia.setAttribute("data-product-url", productUrl);
      }

      // ── METAFIELD MODE: full card-state swap ──
      // Each swatch in metafield mode is a DIFFERENT product. To make
      // "click a color → see THAT color's sizes" work we have to swap:
      //   1. quick-buy contents (pill HTML)
      //   2. #variants_data script (JSON used by refreshAvailability)
      //   3. data-card-unavailable flag on quick-buy
      // Then refresh pill availability against the new data.
      //
      // Variant-color mode swatches (no `card_variant-image` class)
      // share the same #variants_data as their parent, so they skip
      // this block.
      const isMetafieldSwatch = this.classList.contains("card_variant-image");
      const quickBuy = this.mainParent && this.mainParent.querySelector("quick-buy.card-options-wrapper");

      if (isMetafieldSwatch && quickBuy) {
        // 1. Swap pill HTML
        if (variantsHTML) quickBuy.innerHTML = variantsHTML;

        // 2. Swap variants_data
        const innerVariantsScript = this.querySelector("#variants_data_p");
        const outerVariantsScript = this.mainParent.querySelector("#variants_data");
        if (innerVariantsScript && outerVariantsScript) {
          outerVariantsScript.textContent = innerVariantsScript.textContent;
        }

        // 3. Card-unavailable flag based on THIS swatch's product.available
        if (cardAvail === "false") {
          quickBuy.setAttribute("data-card-unavailable", "true");
        } else {
          quickBuy.removeAttribute("data-card-unavailable");
        }

        // 4. Re-parse variants and refresh pill states inline (instead
        // of going through reinitializeQuickBuy which can carry stale
        // closure references). Direct, no indirection.
        let newVariants = [];
        if (outerVariantsScript && outerVariantsScript.textContent) {
          try { newVariants = JSON.parse(outerVariantsScript.textContent); } catch (e) { newVariants = []; }
        }
        quickBuy.variants = newVariants;
        quickBuy.options = quickBuy.querySelectorAll("[data-value]");
        quickBuy.groups = quickBuy.querySelectorAll(".card-grid-options-size");
        quickBuy.selectedOptions = {};
        if (typeof quickBuy.prefillFromFirstAvailable === "function") {
          quickBuy.prefillFromFirstAvailable();
        }
        // Hard-reset disabled on every text pill (variant-images stay
        // server-rendered).
        quickBuy.options.forEach(function (opt) {
          if (opt.tagName !== "VARIANT-IMAGE") opt.removeAttribute("disabled");
        });
        if (typeof quickBuy.refreshAvailability === "function") {
          quickBuy.refreshAvailability();
        }
        // Re-attach click → handleOptionClick to NEW pill elements
        // (the old listeners died with the previous innerHTML).
        quickBuy.options.forEach(function (opt) {
          opt.addEventListener("click", function (e) {
            if (typeof quickBuy.handleOptionClick === "function") {
              e.stopPropagation();
              quickBuy.handleOptionClick(opt);
            }
          });
        });
      } else if (variantsHTML && this.mainVariants) {
        // Variant-color mode: keep the original behavior — replace
        // quick-buy contents but don't touch variants_data.
        this.mainVariants.innerHTML = variantsHTML;
      }

      // ── METAFIELD MODE: title + price swap ──
      // In metafield mode each swatch represents a DIFFERENT product, so
      // the card's TEXT block (title + price) should follow the previewed
      // product too — not just the image. Without this, the card showed
      // the linked product's image but the original product's title and
      // price, which felt broken. Scoped to metafield mode only because
      // variant-color mode swatches all belong to the SAME product (title
      // and price are unchanged across colors).
      if (isMetafieldSwatch) {
        const titleText = this.getAttribute("data-title") || "";
        const priceHTML = this.getAttribute("data-price-html") || "";
        // The title and price live in `.card__information`, which sits
        // OUTSIDE `.card--media` but INSIDE `.card-wrapper`. So we walk
        // up to the wrapper to find them.
        const cardWrapper = (this.mainParent && this.mainParent.closest)
          ? this.mainParent.closest(".card-wrapper")
          : (this.closest ? this.closest(".card-wrapper") : null);
        if (cardWrapper) {
          // Update ALL .card__heading > a inside the wrapper. Pulse
          // renders TWO .card__heading elements per card (an early one
          // inside the first .card__content for layout reasons, and the
          // main visible one inside the second .card__content). Only
          // targeting the FIRST one updates the hidden heading and leaves
          // the visible title untouched. Updating both is safe — they
          // should always carry identical text.
          if (titleText) {
            const headingLinks = cardWrapper.querySelectorAll(".card__heading a");
            if (headingLinks.length > 0) {
              headingLinks.forEach(function(a) { a.textContent = titleText; });
            } else {
              // Fallback: the heading may not have an inner <a> in some
              // card variants — write to the h3 itself.
              cardWrapper.querySelectorAll(".card__heading").forEach(function(h) {
                h.textContent = titleText;
              });
            }
            // Also update the `.card__media` element's aria-label, which
            // mirrors the product title for screen readers + tap-to-nav.
            const cardMediaEl = cardWrapper.querySelector(".card__media[aria-label]");
            if (cardMediaEl) cardMediaEl.setAttribute("aria-label", titleText);
          }
          // Update ALL .price elements (same multi-render reason).
          if (priceHTML) {
            cardWrapper.querySelectorAll(".price").forEach(function(p) {
              p.outerHTML = priceHTML;
            });
          }
        }
      }
    }
    handleState(){
      // Re-query siblings LIVE — same reason as renderUI's re-query of
      // mainMedia/mainBadge. If the card subtree was mutated since this
      // element's constructor ran, the cached `this.siblings` NodeList
      // is stale (points at detached nodes) and clearing their active
      // class is a no-op, leaving two swatches visually selected.
      const liveParent = this.closest(".card--media") || this.mainParent;
      const liveSiblings = liveParent
        ? liveParent.querySelectorAll("variant-image")
        : this.siblings;

      // Toggle the active class on BOTH the variant-image element AND
      // its inner img (when present).
      //   • Metafield mode: variant-image has a real <img> child (the
      //     product swatch image) — the inner-img class was the
      //     original target, kept for backward compat.
      //   • Variant-color mode: variant-image has NO inner <img>
      //     (just a background-color style on the element). Targeting
      //     the inner img would no-op. Adding the class to the element
      //     itself lets CSS attach the selection outline to .color-item
      //     directly.
      liveSiblings.forEach(s => {
        s.classList.remove("current-active-color");
        const sImg = s.querySelector("img");
        if (sImg) sImg.classList.remove("current-active-color");
      });
      this.classList.add("current-active-color");
      const myImg = this.querySelector("img");
      if (myImg) myImg.classList.add("current-active-color");
    }
    reinitializeQuickBuy(){
      const quickBuy = this.mainParent.querySelector("quick-buy");

      if (quickBuy) {
        quickBuy.options = quickBuy.querySelectorAll("[data-value]");
        quickBuy.groups = quickBuy.querySelectorAll(".card-grid-options-size");
        quickBuy.selectedOptions = {};
        quickBuy.variants = JSON.parse(
          this.mainParent.querySelector("#variants_data").textContent
        );
        // Hard reset: clear `disabled` attribute on every size pill so
        // refreshAvailability starts from a clean slate. Without this,
        // a previously-disabled pill from the prior swatch could keep
        // its disabled state if refresh's update doesn't fire for that
        // specific pill (e.g., the pill DOM was the same instance — not
        // recreated by innerHTML swap — and the swap kept the old
        // attribute). Skipping `VARIANT-IMAGE` so color swatches'
        // server-rendered disabled state (the actually-sold-out
        // colorway) doesn't get cleared.
        quickBuy.options.forEach(function(opt) {
          if (opt.tagName !== "VARIANT-IMAGE") {
            opt.removeAttribute("disabled");
          }
        });
        // Re-seed the option pre-fill after a swatch swap so the user
        // can still tap a size and add without first re-tapping a color.
        if (typeof quickBuy.prefillFromFirstAvailable === "function") {
          quickBuy.prefillFromFirstAvailable();
        }
        // Reflect the just-clicked color (this) in selectedOptions so the
        // refresh below knows which sizes are sold out for the actual
        // displayed color, not just the prefilled first-available one.
        // The variant-image's data-value is already lowercase; we need
        // to figure out which option index this swatch represents by
        // looking at its parent .card-grid-options-size data-option.
        var swatchGroup = this.closest && this.closest(".card-grid-options-size");
        if (swatchGroup && this.dataset && this.dataset.value) {
          var swatchIdx = +swatchGroup.dataset.option;
          if (Number.isFinite(swatchIdx)) {
            quickBuy.selectedOptions[swatchIdx] = this.dataset.value;
          }
        }
        if (typeof quickBuy.refreshAvailability === "function") {
          quickBuy.refreshAvailability();
        }

        quickBuy.options.forEach(option => {
          option.addEventListener("click", (e) => {
            e.stopPropagation();
            quickBuy.handleOptionClick(option);
          });
        });
      }
    }
  })
}

if(!customElements.get("quick-buy")){
  customElements.define("quick-buy",class QuickBuy extends HTMLElement{
    constructor(){
      super();
      this.mainParent = this.closest(".product-card-wrapper");
      this.options = this.mainParent.querySelectorAll("[data-value]");
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.groups = this.mainParent.querySelectorAll(".card-grid-options-size");
      this.selectedOptions = {};
      this.variants = this.mainParent.querySelector("#variants_data") ? JSON.parse(this.mainParent.querySelector("#variants_data").textContent) : {};
      this.prefillFromFirstAvailable();
    }
    /* Pre-populate selectedOptions with the option values of the first
       available variant. Without this, a user who taps a size pill
       WITHOUT first tapping a color swatch hits handleOptionClick with
       selectedOptions = { sizeIdx: 'small' } — but variants.find()
       requires every option to match, so selectedOptions[colorIdx] being
       undefined silently fails the match and nothing adds to cart. By
       pre-filling with the first-available variant we make "click size
       and it adds the currently-displayed product" work out of the box.
       Falls back to variants[0] when no variant is available (entire
       product is sold out — the click-then-fail path is fine then). */
    prefillFromFirstAvailable(){
      if (!Array.isArray(this.variants) || !this.variants.length) return;
      var seed = this.variants.find(function(v){ return v && v.available; }) || this.variants[0];
      if (!seed || !Array.isArray(seed.options)) return;
      for (var i = 0; i < seed.options.length; i++) {
        var v = seed.options[i];
        if (typeof v === "string") this.selectedOptions[i] = v.toLowerCase();
      }
    }
    /* Recompute the `disabled` state of every option pill based on the
       currently-selected combination. For each pill we look up whether
       a variant exists that matches (currentSelection ∪ {pillGroup:
       pillValue}) AND is available. If not, the pill gets `disabled`
       added (so the strike-through CSS fires); otherwise it's removed.
       This fixes the case where clicking a Blue swatch on a product
       where Blue-Small is sold out should cross out Small — Liquid's
       server-rendered `value.variant.available` only knows about the
       first-color variant of each size, so it can't reflect per-color
       availability accurately. Skips VARIANT-IMAGE swatches themselves
       (they represent the COLOR axis being chosen, not refreshed). */
    refreshAvailability(){
      if (!Array.isArray(this.variants)) return;
      // Card-level sold-out override: when card_product.available is
      // false (Liquid emits data-card-unavailable="true"), the merchant
      // / Shopify has marked the entire product as unavailable —
      // disable every pill regardless of what individual variant.available
      // reports. Shopify's per-variant availability can lie when
      // continue-selling-when-out-of-stock is enabled on some variants
      // and the merchant manually flags the product sold out; trusting
      // card_product.available avoids "X says it's available but the
      // product banner says SOLD OUT" inconsistencies.
      var cardUnavailable = this.dataset.cardUnavailable === "true";
      var self = this;
      this.options.forEach(function(option){
        if (option.tagName === "VARIANT-IMAGE") return;
        var group = option.closest(".card-grid-options-size");
        if (!group) return;
        var groupIdx = +group.dataset.option;
        if (!Number.isFinite(groupIdx)) return;
        if (cardUnavailable) {
          option.setAttribute("disabled", "");
          return;
        }
        var pillValue = (option.dataset.value || "").toLowerCase();
        var hypo = Object.assign({}, self.selectedOptions);
        hypo[groupIdx] = pillValue;
        var variant = self.variants.find(function(v){
          if (!v || !Array.isArray(v.options)) return false;
          return v.options.every(function(opt, i){
            var sel = hypo[i];
            if (sel === undefined) return false;
            return opt && opt.toLowerCase() === sel;
          });
        });
        if (!variant || !variant.available) {
          option.setAttribute("disabled", "");
        } else {
          option.removeAttribute("disabled");
        }
      });
    }
    connectedCallback(){
      // Multi-option products (e.g. bundles with 2+ option groups) bail
      // out entirely: no hover-expand, no panel; the capture-phase click
      // handler in the doc-level delegation navigates to the PDP. The
      // Liquid template skips rendering the pills for these too, so the
      // visual state is just the static + button.
      if (this.dataset.multiOption === "true") {
        return;
      }
      // Initial availability pass for the prefilled selection. Liquid's
      // server-rendered disabled state can be wrong for per-color sizes
      // (it only knows the first-color variant). This corrects it for
      // the currently-displayed color.
      this.refreshAvailability();
      // Single-variant shortcut: clicking + adds the variant directly
      // (no need to tap the pill). We attach the shortcut handler with
      // stopPropagation so the regular click-adds-active listener below
      // doesn't ALSO fire and toggle the panel. Importantly we DON'T
      // return early anymore — the multi-variant hover listeners below
      // also attach for single-variant products, so hovering the +
      // expands the panel and shows the single pill (e.g. "One Size").
      // Merchants get visual feedback that the product has options too.
      if (Array.isArray(this.variants) && this.variants.length === 1) {
        this.classList.add("is-single-variant");
        const addBtn = this.querySelector(".quick-add-btn");
        if (addBtn) {
          const self = this;
          addBtn.addEventListener("click", function(e) {
            e.preventDefault();
            e.stopPropagation();
            // Close any other product's open panel first so this direct
            // add fires on the same tap rather than requiring a second.
            var others = document.querySelectorAll("quick-buy.card-options-wrapper.quick-buy--active");
            for (var k = 0; k < others.length; k++) {
              if (others[k] !== self) others[k].classList.remove("quick-buy--active");
            }
            self.handleSingleVariantAdd();
          });
        }
        // Single-variant products skip the hover-to-open panel listeners
        // entirely — clicking + adds directly with no panel preview. The
        // option pill (e.g. "One Size") is purely informational and never
        // needs to be interacted with.
        return;
      }
      const addEvents = ["mouseover","click"];
      const removeEvents = ["mouseleave"];
      addEvents.forEach(event =>{
        this.addEventListener(event,()=>{
          this.classList.add("quick-buy--active")
        })
      })
      removeEvents.forEach(event =>{
        this.mainParent.addEventListener(event,()=>{
          this.classList.remove("quick-buy--active")
        })
      })
      this.options.forEach(option => {
        option.addEventListener("click", (e) => {
          e.stopPropagation();
          this.handleOptionClick(option);
        });
      });
    }
    handleSingleVariantAdd(){
      const variant = this.variants[0];
      if (!variant) return;
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      const formData = new FormData();
      formData.append("id", variant.id);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;
      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (!this.cart) { window.location = window.routes.cart_url; return; }
          this.cart.renderContents(response);
        })
        .catch((e) => { console.error(e); })
        .finally(() => {
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        });
    }
    handleOptionClick(option){
      const value = option.dataset.value;

      const group = option.closest(".card-grid-options-size");
      // Metafield-mode variant-image swatches live inside `.card_colors`,
      // NOT inside `.card-grid-options-size`. Without this guard the
      // next line (`+ group.dataset.option`) used to throw silently
      // ("Cannot read properties of null"), aborting the rest of the
      // handler. We bail out cleanly instead — the visual preview is
      // handled by variant-image's own click listener (renderUI).
      if (!group) return;
      const groupIndex = + group.dataset.option;


      this.selectedOptions[groupIndex] = value;

      group.querySelectorAll("[data-value]").forEach(opt => {
        opt.classList.remove("is-active");
      });

      option.classList.add("is-active");

      // Re-evaluate disabled state on every pill now that selectedOptions
      // changed. Clicking Blue → sizes that don't have an available
      // Blue variant get crossed out; clicking a size doesn't usually
      // change color availability but we refresh anyway for consistency.
      this.refreshAvailability();
      const matchedVariant = this.variants.find(variant => {
        return variant.options.every((opt, index) => {
          return this.selectedOptions[index] === opt.toLowerCase();
        });
      });
      this.selectedVariant = matchedVariant;
      if(!matchedVariant) return;
      // Skip auto-add for ALL <variant-image> swatches:
      //  • Variant-color mode: clicking a color SELECTS it; user commits via
      //    size pill / + button.
      //  • Metafield mode: clicking navigates to the linked product's PDP —
      //    handled in variant-image's own click listener and never reaches
      //    add-to-cart from here. Previously this branch let metafield clicks
      //    fall through to the auto-add path below, which was confusing because
      //    each "swatch" is a different product and the user expected to land
      //    on its PDP, not silently add the parent product to cart.
      if (option.tagName === "VARIANT-IMAGE") {
        return;
      }
      const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        const formData = new FormData();
        formData.append("id",matchedVariant.id)
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
      config.body = formData;
      fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }
        this.cart.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
      });
    }
  })
}

/* Document-level click backup for product-card <variant-image> swatches.
   The custom-element handler in connectedCallback can fail to fire (or
   succeed but produce a no-op visual swap) for several reasons:
     • The custom element wasn't upgraded yet (script ordering race)
     • The constructor's cached refs are stale and innerHTML writes go nowhere
     • Custom-element upgrade timing means QuickBuy's listener got there
       FIRST and the variant-image listener's stopImmediatePropagation
       can't stop it retroactively
   Symptom: the user reports the variant DOES become selected (so add to
   cart picks the right SKU) but the visible image NEVER swaps.

   This doc-level handler is fully self-contained — it doesn't call the
   custom-element's methods, it just does the DOM mutations directly.
   That way it works regardless of custom-element upgrade state.
   It runs UNCONDITIONALLY (no flag dance). Both this listener and the
   custom-element's renderUI may both run on the same click — that's
   fine, the DOM writes are idempotent (writing the same innerHTML twice
   is identical to writing it once). */
(function() {
  if (!document || !document.addEventListener) return;
  document.addEventListener("click", function(e) {
    if (!e.target || !e.target.closest) return;
    var vi = e.target.closest("variant-image");
    if (!vi) return;

    // Walk up to the card. `.card--media` is the canonical wrapper for
    // image-bearing cards; fall back to `.card-wrapper` (the outermost
    // wrapper, present even on text-only cards) for safety.
    var card = vi.closest(".card--media") || vi.closest(".card-wrapper");
    if (!card) return;

    var mediaHTML = vi.getAttribute("data-images") || "";
    var badgeHTML = vi.getAttribute("data-badge") || "";
    var productUrl = vi.getAttribute("data-product") || "";

    // Only swap if there's actual image HTML — empty data-images would
    // wipe the visible image to a blank box. Better to leave the
    // current image showing than to flash blank.
    if (mediaHTML.trim()) {
      var media = card.querySelector(".media");
      if (media) media.innerHTML = mediaHTML;
    }

    var badge = card.querySelector(".card__badge");
    if (badge) badge.innerHTML = badgeHTML;

    if (productUrl) {
      var anchors = card.querySelectorAll("a");
      anchors.forEach(function(a) { a.href = productUrl; });
      var cardMedia = card.querySelector(".card__media[data-product-url]");
      if (cardMedia) cardMedia.setAttribute("data-product-url", productUrl);
    }

    // Active-state toggle across all swatch siblings on this card.
    card.querySelectorAll("variant-image").forEach(function(sibling) {
      sibling.classList.remove("current-active-color");
      var sImg = sibling.querySelector("img");
      if (sImg) sImg.classList.remove("current-active-color");
    });
    vi.classList.add("current-active-color");
    var myImg = vi.querySelector("img");
    if (myImg) myImg.classList.add("current-active-color");

    // ── METAFIELD MODE: title + price swap ──
    // Mirrors the logic in VariantImage.renderUI. Only fires for
    // `card_variant-image` swatches (metafield mode); variant-color
    // mode swatches all share the same product so title/price are
    // unchanged across colors.
    if (vi.classList.contains("card_variant-image")) {
      var titleText = vi.getAttribute("data-title") || "";
      var priceHTML = vi.getAttribute("data-price-html") || "";
      // Title and price live in `.card__information` which is INSIDE
      // `.card-wrapper` but OUTSIDE `.card--media`. Walk up to the
      // wrapper.
      var cardWrapper = vi.closest(".card-wrapper");
      if (cardWrapper) {
        // Update ALL .card__heading > a (Pulse renders two .card__heading
        // elements per card — see VariantImage.renderUI for the full
        // explanation). Only updating the first one leaves the visible
        // title untouched.
        if (titleText) {
          var headingLinks = cardWrapper.querySelectorAll(".card__heading a");
          if (headingLinks.length > 0) {
            headingLinks.forEach(function(a) { a.textContent = titleText; });
          } else {
            cardWrapper.querySelectorAll(".card__heading").forEach(function(h) {
              h.textContent = titleText;
            });
          }
          var cardMediaEl = cardWrapper.querySelector(".card__media[aria-label]");
          if (cardMediaEl) cardMediaEl.setAttribute("aria-label", titleText);
        }
        if (priceHTML) {
          cardWrapper.querySelectorAll(".price").forEach(function(p) {
            p.outerHTML = priceHTML;
          });
        }
      }
    }
  }, false);
})();

/* Document-level hover backup for product-card <quick-buy>.
   The custom-element listener sometimes fails to attach on the live
   storefront (custom element timing on initial page load) — but the
   theme editor re-renders sections constantly so it always works
   there. This document delegation toggles the same active class so
   hover works regardless of custom element state. Idempotent. */
(function() {
  if (!document || !document.addEventListener) return;
  document.addEventListener("mouseover", function(e) {
    if (!e.target || !e.target.closest) return;
    var quickBuy = e.target.closest("quick-buy.card-options-wrapper");
    if (!quickBuy) return;
    // Skip products that don't have inline option pills (e.g., size-
    // less single-variant products or color-only products). For
    // those, hover should do nothing — the user just clicks + to add
    // the only available variant. Expanding an empty panel looks
    // broken.
    if (!quickBuy.querySelector(".card-grid-options-size [data-value]")) return;
    // Also skip single-variant products that DO have a pill rendered
    // (e.g., "Size: One Size" — the variant count is 1 even though a
    // pill is shown). Liquid sets data-single-variant="true" on these
    // — no runtime JSON parsing needed.
    if (quickBuy.dataset.singleVariant === "true") return;
    // (Earlier Swiper-driven sliders fired synthetic hover during
    // drag and needed this skip-out. With both related-products and
    // featured-collection now using native CSS scroll, the browser
    // doesn't fire hover during scroll, so no skip is required.)
    quickBuy.classList.add("quick-buy--active");
  });
  document.addEventListener("mouseout", function(e) {
    if (!e.target || !e.target.closest) return;
    var card = e.target.closest(".product-card-wrapper");
    if (!card) return;
    if (e.relatedTarget && card.contains(e.relatedTarget)) return;
    var quickBuy = card.querySelector("quick-buy.card-options-wrapper");
    if (quickBuy) quickBuy.classList.remove("quick-buy--active");
  });
})();

/* Tap-outside dismissal for the open quick-buy panel.
   On mobile (no mouseleave), tapping a subcategory link / nav item /
   anything outside any quick-buy used to require TWO taps: the first
   tap collapsed the open panel (some browsers route the touch into
   defocusing the previously-hovered element) but didn't fire the
   target's click; the second tap actually navigated. Hooking into
   pointerdown — which fires BEFORE the click in the touch sequence —
   closes the panel proactively without consuming the tap, so the same
   gesture both closes the panel AND triggers the link/button.

   Keep the panel open when the tap lands ANYWHERE inside the same
   `.product-card-wrapper`. The original check ("is the tap inside
   <quick-buy>?") was too narrow — color swatches in variant-color
   mode sit in `.card_color`, OUTSIDE quick-buy. Tapping a color
   closed the panel BEFORE variant-image's click handler could update
   the size pills, so users saw the panel collapse and assumed the
   sizes didn't refresh. Scoping to the whole card-wrapper means any
   color/swatch interaction on the SAME card keeps its panel visible,
   while taps on other cards / page chrome / nav still close it. */
(function () {
  if (!document || !document.addEventListener) return;
  if (typeof window.PointerEvent === "undefined") return;
  document.addEventListener("pointerdown", function (e) {
    if (!e.target || !e.target.closest) return;
    var openPanels = document.querySelectorAll("quick-buy.card-options-wrapper.quick-buy--active");
    if (!openPanels.length) return;
    var tappedCard = e.target.closest(".product-card-wrapper");
    for (var i = 0; i < openPanels.length; i++) {
      var panelCard = openPanels[i].closest(".product-card-wrapper");
      if (tappedCard && tappedCard === panelCard) continue;
      openPanels[i].classList.remove("quick-buy--active");
    }
  }, true);
})();

/* Document-level click delegation for the + button (quick-add).
   Routes:
     • Multi-variant product with inline pills → expand the panel so
       the user can pick a size (custom element does this too when
       registered).
     • Single-variant / no-option product → add the variant directly.
   If the custom element registered, its handler calls preventDefault
   + stopPropagation first and this delegation never fires. */
(function() {
  if (!document || !document.addEventListener) return;
  // Capture-phase handler: runs BEFORE the custom element's bubble-phase
  // click listener that adds `quick-buy--active`. For multi-option
  // products (bundles, multi-dimension), we navigate to the PDP instead
  // of expanding the panel — there's no useful quick-add UX when 2+
  // option groups need selecting in a small bottom-right panel.
  document.addEventListener("click", function(e) {
    if (!e.target || !e.target.closest) return;
    var addBtn = e.target.closest(".quick-add-btn");
    if (!addBtn) return;
    var quickBuy = addBtn.closest("quick-buy.card-options-wrapper");
    if (!quickBuy) return;
    if (quickBuy.dataset.multiOption !== "true") return;
    e.preventDefault();
    e.stopPropagation();
    var url = quickBuy.dataset.productUrl;
    if (url) window.location.href = url;
  }, true);
  document.addEventListener("click", function(e) {
    if (!e.target || !e.target.closest) return;
    var addBtn = e.target.closest(".quick-add-btn");
    if (!addBtn) return;
    var quickBuy = addBtn.closest("quick-buy.card-options-wrapper");
    if (!quickBuy) return;
    // Multi-option: handled by the capture-phase listener above.
    if (quickBuy.dataset.multiOption === "true") return;
    e.preventDefault();
    e.stopPropagation();

    var hasInlinePills = !!quickBuy.querySelector(".card-grid-options-size [data-value]");
    // Detect single-variant products even when they have an option pill
    // rendered (e.g. a product with "Size: One Size"). Liquid sets the
    // attribute server-side, no runtime parsing needed.
    var isSingleVariant = quickBuy.dataset.singleVariant === "true";
    if (hasInlinePills && !isSingleVariant) {
      // Close any other open panels first — only one quick-buy should
      // be visible at a time. On desktop this happens automatically via
      // the per-card mouseleave handler, but on touch devices (no
      // mouseleave) tapping + on a second product would leave the first
      // product's panel still expanded, covering its image and title.
      var others = document.querySelectorAll("quick-buy.card-options-wrapper.quick-buy--active");
      for (var i = 0; i < others.length; i++) {
        if (others[i] !== quickBuy) others[i].classList.remove("quick-buy--active");
      }
      // Expand the panel so the visitor can pick a size.
      quickBuy.classList.add("quick-buy--active");
      return;
    }

    // No inline pills. Add the first available variant directly — and
    // close any open panel on a different product first, so a user can
    // tap + on a no-size product while another product's panel is open
    // and have the add fire on the SAME tap (no two-tap confusion).
    var others = document.querySelectorAll("quick-buy.card-options-wrapper.quick-buy--active");
    for (var k = 0; k < others.length; k++) {
      if (others[k] !== quickBuy) others[k].classList.remove("quick-buy--active");
    }
    var card = quickBuy.closest(".product-card-wrapper");
    if (!card) return;
    var variantsScript = card.querySelector("#variants_data");
    if (!variantsScript) return;
    var variants;
    try { variants = JSON.parse(variantsScript.textContent); } catch (err) { return; }
    if (!Array.isArray(variants) || variants.length === 0) return;
    var variant = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) { variant = variants[i]; break; }
    }
    if (!variant) variant = variants[0];
    if (!variant) return;

    var cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
    var config = (typeof fetchConfig === "function")
      ? fetchConfig("javascript")
      : { headers: { "X-Requested-With": "XMLHttpRequest" }, method: "POST" };
    if (config.headers) {
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
    }
    var formData = new FormData();
    formData.append("id", variant.id);
    if (cart && cart.getSectionsToRender) {
      formData.append("sections", cart.getSectionsToRender().map(function(s) { return s.id; }));
      formData.append("sections_url", window.location.pathname);
      if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
    }
    config.body = formData;
    fetch((window.routes && window.routes.cart_add_url) || "/cart/add.js", config)
      .then(function(r) { return r.json(); })
      .then(function(r) {
        if (!cart) {
          window.location = (window.routes && window.routes.cart_url) || "/cart";
          return;
        }
        if (cart.renderContents) cart.renderContents(r);
      })
      .catch(function(err) { console.error(err); })
      .finally(function() {
        if (cart && cart.classList && cart.classList.contains("is-empty")) {
          cart.classList.remove("is-empty");
        }
      });
  });
})();

/* Document-level click delegation backup for size-pill add-to-cart.
   If quick-buy's connectedCallback failed to attach its click
   listeners (custom element timing race on storefront), the click
   bubbles to document here. We set the option active, look up the
   matched variant from inline variants_data, and POST to cart.
   If the custom element DID attach, its handler calls
   e.stopPropagation() first and this delegation never runs. */
(function() {
  if (!document || !document.addEventListener) return;
  document.addEventListener("click", function(e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest("button[data-value]");
    if (!btn) return;
    var quickBuy = btn.closest("quick-buy.card-options-wrapper");
    if (!quickBuy) return;
    e.preventDefault();
    e.stopPropagation();

    var group = btn.closest(".card-grid-options-size");
    if (group) {
      var siblings = group.querySelectorAll("[data-value]");
      for (var i = 0; i < siblings.length; i++) {
        siblings[i].classList.remove("is-active");
      }
    }
    btn.classList.add("is-active");

    var card = quickBuy.closest(".product-card-wrapper");
    if (!card) return;
    var selected = {};
    var groups = card.querySelectorAll(".card-grid-options-size");
    for (var g = 0; g < groups.length; g++) {
      var idx = +groups[g].dataset.option;
      var active = groups[g].querySelector("[data-value].is-active");
      if (active) selected[idx] = active.dataset.value;
    }

    var variantsScript = card.querySelector("#variants_data");
    if (!variantsScript) return;
    var variants;
    try { variants = JSON.parse(variantsScript.textContent); } catch (err) { return; }
    if (!Array.isArray(variants)) return;

    var matched = null;
    for (var v = 0; v < variants.length; v++) {
      var allMatch = true;
      for (var k = 0; k < variants[v].options.length; k++) {
        if (selected[k] !== variants[v].options[k].toLowerCase()) {
          allMatch = false;
          break;
        }
      }
      if (allMatch) { matched = variants[v]; break; }
    }
    if (!matched) return;

    var cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
    var config = (typeof fetchConfig === "function")
      ? fetchConfig("javascript")
      : { headers: { "X-Requested-With": "XMLHttpRequest" }, method: "POST" };
    if (config.headers) {
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
    }
    var formData = new FormData();
    formData.append("id", matched.id);
    if (cart && cart.getSectionsToRender) {
      formData.append("sections", cart.getSectionsToRender().map(function(s) { return s.id; }));
      formData.append("sections_url", window.location.pathname);
      if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
    }
    config.body = formData;
    fetch((window.routes && window.routes.cart_add_url) || "/cart/add.js", config)
      .then(function(r) { return r.json(); })
      .then(function(r) {
        if (!cart) {
          window.location = (window.routes && window.routes.cart_url) || "/cart";
          return;
        }
        if (cart.renderContents) cart.renderContents(r);
      })
      .catch(function(err) { console.error(err); })
      .finally(function() {
        if (cart && cart.classList && cart.classList.contains("is-empty")) {
          cart.classList.remove("is-empty");
        }
      });
  });
})();

/* ── Card image → PDP navigation ──
   `.card__media[data-product-url]` is rendered by snippets/card-product.liquid.
   We can't simply wrap `.card__media` in <a> because the gallery inside
   contains <button> chevrons and HTML5 forbids interactive content inside
   <a> (browsers silently abort the anchor).

   The handler runs at capture phase on the gallery TRACK (most likely
   click target) and also at document level as a safety net. Either way,
   we navigate when the click is NOT on an interactive descendant
   (chevron, swatch, +, size pill, link inside the card content) — those
   keep their own behavior. A small `moved` flag (in the local capture
   loop) and `defaultPrevented` check together prevent swipe-to-click
   misfires. */
(function () {
  function looksInteractive(target) {
    if (!target || !target.closest) return false;
    return !!target.closest(
      'a, button, input, select, textarea, label, [role="button"],' +
      ' variant-image, quick-buy, .pulse-card-gallery__chevron,' +
      ' .quick-add-btn, .card-grid-options-size'
    );
  }
  function navigateFromMedia(target) {
    if (!target || !target.closest) return false;
    var media = target.closest('.card__media[data-product-url]');
    if (!media) return false;
    var url = media.getAttribute('data-product-url');
    if (!url) return false;
    window.location.href = url;
    return true;
  }
  // Document-level click handler — primary path. Capture phase so we
  // run before any bubble-phase listener (and our own .closest() guard
  // makes sure we never hijack interactive descendants).
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    if (e.button !== undefined && e.button !== 0) return;
    if (looksInteractive(e.target)) return;
    navigateFromMedia(e.target);
  }, true);
  // Keyboard activation — role="link" + tabindex="0" tells AT this is a
  // link, so support Enter / Space too.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var media = e.target && e.target.closest && e.target.closest('.card__media[data-product-url]');
    if (!media) return;
    if (e.target !== media) return; // only when focus is ON .card__media itself
    e.preventDefault();
    var url = media.getAttribute('data-product-url');
    if (url) window.location.href = url;
  });
})();
